/**
 * Editor state reducer + helpers.
 *
 * Source of truth for the editor's UI. The `timeline` slice is what we
 * autosave back to the backend; everything else (selection, playhead,
 * render status) is local UI state.
 */
import { useCallback, useMemo, useReducer } from 'react';

import {
  AnyClip, AspectRatio, AudioClip, ClipFraming, ClipSelection, ProjectAsset,
  ProjectRender, RenderStatus, TextClip, Timeline, TrackKind, Transition,
  VideoClip, VideoProject,
} from '../../types/editor';
import { ProgressPhase } from '../../types/progress';
import { DEFAULT_FRAMING, defaultTimeline, dimensionsForAspect, migrateTimeline, withCoverFraming } from './migrateTimeline';

export const DEFAULT_IMAGE_DUR = 5;
export const DEFAULT_TRANSITION_DUR = 0.5;
/* Minimum total duration a clip must have before it can be split —
 * each half needs to be at least half this. 0.4s → halves of 0.2s,
 * which matches MIN_CLIP_DURATION in the Timeline trim handler. */
export const MIN_CLIP_DUR_FOR_SPLIT = 0.4;
/* Audio caps at 1 because the v1 render pipeline mixes a single
 * track. Video has no cap — add as many clips as the project
 * needs. */
export const MAX_AUDIO_CLIPS = 1;
export const MAX_PROJECT_DURATION = 600;

const ASPECT_PRESETS: Record<AspectRatio, { width: number; height: number }> = {
  '9:16': { width: 1080, height: 1920 },
  '1:1':  { width: 1080, height: 1080 },
  '16:9': { width: 1920, height: 1080 },
  '4:5':  { width: 1080, height: 1350 },
};

export const ALL_ASPECTS: AspectRatio[] = ['9:16', '1:1', '16:9', '4:5'];

// ---------- State ----------

export interface EditorMeta {
  name: string;
  aspect: AspectRatio;
  width: number;
  height: number;
}

/**
 * Render display status — wider than the backend's persisted `RenderStatus`
 * because the UI mirrors live progress events (preparing/encoding/uploading
 * etc.) for the user as they stream in.
 */
export type RenderDisplayStatus = RenderStatus | ProgressPhase;

export interface RenderState {
  status: RenderDisplayStatus;
  percent: number;
  message: string;
  error: string | null;
  taskId: string | null;
  attempt: number;
}

export interface SaveState {
  dirty: boolean;
  saving: boolean;
  lastSavedAt: number | null;
  error: string | null;
}

export interface EditorState {
  projectId: number | null;
  meta: EditorMeta;
  timeline: Timeline;
  assets: ProjectAsset[];
  /**
   * The aspect tab the user is currently editing. Drives the preview
   * canvas dimensions AND which `timeline.aspects[currentAspect]
   * .clipFraming` slot the framing controls write to.
   * Independent of meta.aspect (which is just the value we persist
   * to disk for "default open").
   */
  currentAspect: AspectRatio;
  /** Per-aspect rendered MP4 records, hydrated from VideoProject.renders. */
  renders: ProjectRender[];
  /**
   * Per-aspect bump counter for useRenderProgress. Bumping the entry
   * for an aspect forces useRenderProgress to tear down and reopen its
   * EventSource for that aspect — used after dispatching a fresh
   * render so we don't get stuck reading the prior task's terminal
   * event.
   */
  renderAttempts: Record<AspectRatio, number>;
  selectedClip: ClipSelection | null;
  playhead: number;
  playing: boolean;
  pxPerSec: number;
  renderState: RenderState;
  saveState: SaveState;
}

const emptyAttempts = (): Record<AspectRatio, number> => ({
  '9:16': 0, '1:1': 0, '16:9': 0, '4:5': 0,
});

export const initialState: EditorState = {
  projectId: null,
  meta: { name: '', aspect: '9:16', width: 1080, height: 1920 },
  timeline: defaultTimeline(),
  assets: [],
  currentAspect: '9:16',
  renders: [],
  renderAttempts: emptyAttempts(),
  selectedClip: null,
  playhead: 0,
  playing: false,
  pxPerSec: 60,
  renderState: { status: 'idle', percent: 0, message: '', error: null, taskId: null, attempt: 0 },
  saveState: { dirty: false, saving: false, lastSavedAt: null, error: null },
};

// ---------- Actions ----------

type EditorAction =
  | { type: 'LOAD_PROJECT'; project: VideoProject }
  | { type: 'SET_META'; patch: Partial<EditorMeta> }
  | { type: 'SET_CURRENT_ASPECT'; aspect: AspectRatio }
  | { type: 'UPDATE_FRAMING'; aspect: AspectRatio; clipId: string; patch: Partial<ClipFraming> }
  | { type: 'RESET_FRAMING'; aspect: AspectRatio; clipId: string }
  | { type: 'SET_RENDERS'; renders: ProjectRender[] }
  | { type: 'ADD_ASSET'; asset: ProjectAsset }
  | { type: 'REORDER_ASSETS'; fromIdx: number; toIdx: number }
  | { type: 'DELETE_ASSET'; assetId: number }
  | { type: 'ADD_CLIP'; asset: ProjectAsset; startAt?: number }
  | { type: 'ADD_TEXT'; startAt: number }
  | { type: 'MOVE_CLIP'; track: TrackKind; clipId: string; patch: Partial<AnyClip> }
  | { type: 'UPDATE_CLIP'; track: TrackKind; clipId: string; patch: Partial<AnyClip> }
  | { type: 'TRIM_CLIP'; track: TrackKind; clipId: string; patch: Partial<AnyClip> }
  | { type: 'DELETE_CLIP'; track: TrackKind; clipId: string }
  | { type: 'SPLIT_CLIP'; track: TrackKind; clipId: string }
  | { type: 'SELECT_CLIP'; selection: ClipSelection | null }
  | { type: 'SEEK'; t: number }
  | { type: 'SET_PLAYING'; playing: boolean }
  | { type: 'SET_PX_PER_SEC'; px: number }
  | { type: 'MARK_SAVING' }
  | { type: 'MARK_SAVED' }
  | { type: 'MARK_SAVE_ERROR'; error: string }
  | { type: 'SET_RENDER'; patch: Partial<RenderState> }
  | { type: 'BUMP_RENDER_ATTEMPT'; aspect: AspectRatio };

// ---------- Helpers ----------

/* Style of the LAST-EDITED text clip, persisted so new text clips
 * start from what the user last used (font, colors, radius, size,
 * nudge) instead of factory defaults. */
const TEXT_DEFAULTS_KEY = 'megaphony.editor.textDefaults';
type TextStyleDefaults = Partial<Pick<TextClip,
  'font' | 'color' | 'outlineColor' | 'bgColor' | 'bgRadius' | 'fontSize' | 'textOffsetY'>>;

export function loadTextDefaults(): TextStyleDefaults {
  try {
    const raw = window.localStorage.getItem(TEXT_DEFAULTS_KEY);
    return raw ? (JSON.parse(raw) as TextStyleDefaults) : {};
  } catch {
    return {};
  }
}

export function saveTextDefaults(patch: TextStyleDefaults): void {
  try {
    window.localStorage.setItem(
      TEXT_DEFAULTS_KEY,
      JSON.stringify({ ...loadTextDefaults(), ...patch }),
    );
  } catch { /* private mode — defaults just don't persist */ }
}

function uid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

function clipDur(c: AnyClip): number {
  return Math.max(0, Number(c.trimOut) - Number(c.trimIn));
}

function recomputeDuration(timeline: Timeline): Timeline {
  let end = 0;
  for (const c of timeline.video.clips) end = Math.max(end, Number(c.start) + clipDur(c));
  for (const c of timeline.audio.clips) end = Math.max(end, Number(c.start) + clipDur(c));
  return {
    ...timeline,
    project: { ...timeline.project, duration: Math.round(end * 100) / 100 },
  };
}

function markDirty(state: EditorState, timeline: Timeline): EditorState {
  return {
    ...state,
    timeline: recomputeDuration(timeline),
    saveState: { ...state.saveState, dirty: true },
  };
}

function buildClipFromAsset(
  asset: ProjectAsset,
  track: TrackKind,
  startAt: number,
): VideoClip | AudioClip {
  if (track === 'audio') {
    const audio: AudioClip = {
      id: uid('ac'),
      assetId: asset.id,
      trimIn: 0,
      trimOut: asset.duration || 30,
      start: startAt,
      volume: 1.0,
    };
    return audio;
  }
  const isImage = asset.kind === 'image';
  const trans: Transition = { type: 'cut', duration: DEFAULT_TRANSITION_DUR };
  const video: VideoClip = {
    id: uid('vc'),
    assetId: asset.id,
    trimIn: 0,
    trimOut: isImage ? DEFAULT_IMAGE_DUR : (asset.duration || DEFAULT_IMAGE_DUR),
    start: startAt,
    transitionIn: trans,
    // Original soundtrack plays at 100% by default; the inspector's
    // "Original audio" slider mutes or boosts it (0–200%).
    ...(isImage ? {} : { volume: 1.0 }),
  };
  return video;
}

// ---------- Reducer ----------

function reducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'LOAD_PROJECT': {
      const p = action.project;
      const aspect = (p.aspect as AspectRatio) || '9:16';
      const timeline = migrateTimeline(p.timeline || defaultTimeline(aspect));
      // Normalize canvas dims to the active aspect on load — saved
      // timelines can carry stale project.width/height (e.g. written
      // while a different aspect tab was open), which otherwise
      // letterboxes the preview until the user re-clicks the aspect.
      // Mirrors what SET_CURRENT_ASPECT does on every tab switch.
      const dims = dimensionsForAspect(aspect);
      timeline.project.aspect = aspect;
      timeline.project.width = dims.width;
      timeline.project.height = dims.height;
      const renders = p.renders || [];
      // Hydrate render status from the active aspect's row (if any) so
      // the legacy single render-state UI keeps reflecting something
      // sensible. The new per-aspect UI reads `state.renders` directly.
      const activeRender = renders.find((r) => r.aspect === aspect);
      return {
        ...initialState,
        projectId: p.id,
        meta: { name: p.name, aspect, width: dims.width, height: dims.height },
        timeline: recomputeDuration(timeline),
        assets: p.assets || [],
        currentAspect: aspect,
        renders,
        renderAttempts: emptyAttempts(),
        renderState: {
          ...initialState.renderState,
          status: activeRender?.status || 'idle',
          message: activeRender?.error || '',
        },
        saveState: { dirty: false, saving: false, lastSavedAt: null, error: null },
      };
    }

    case 'SET_META': {
      const meta: EditorMeta = { ...state.meta, ...action.patch };
      if (action.patch.aspect && ASPECT_PRESETS[action.patch.aspect]) {
        const preset = ASPECT_PRESETS[action.patch.aspect];
        meta.width = preset.width;
        meta.height = preset.height;
      }
      const timeline: Timeline = {
        ...state.timeline,
        project: {
          ...state.timeline.project,
          aspect: meta.aspect,
          width: meta.width,
          height: meta.height,
        },
      };
      // Changing the persisted "default" aspect also flips the active
      // editing tab so the canvas dimensions and framing controls match.
      return {
        ...state, meta, timeline,
        currentAspect: action.patch.aspect ?? state.currentAspect,
        saveState: { ...state.saveState, dirty: true },
      };
    }

    case 'SET_CURRENT_ASPECT': {
      // Swap the canvas to a different aspect WITHOUT marking the
      // project dirty — switching tabs is read-only navigation. The
      // canvas dims update; nothing gets persisted until the user
      // actually customizes framing or picks a different "default"
      // aspect from the select.
      const dims = dimensionsForAspect(action.aspect);
      const timeline: Timeline = {
        ...state.timeline,
        project: {
          ...state.timeline.project,
          aspect: action.aspect,
          width: dims.width,
          height: dims.height,
        },
      };
      return { ...state, currentAspect: action.aspect, timeline };
    }

    case 'UPDATE_FRAMING': {
      const { aspect, clipId, patch } = action;
      const slot = state.timeline.aspects[aspect]?.clipFraming[clipId] || DEFAULT_FRAMING;
      const next: ClipFraming = { ...slot, ...patch };
      const aspectsNext = {
        ...state.timeline.aspects,
        [aspect]: {
          clipFraming: {
            ...state.timeline.aspects[aspect]?.clipFraming,
            [clipId]: next,
          },
        },
      };
      return markDirty(state, { ...state.timeline, aspects: aspectsNext });
    }

    case 'RESET_FRAMING': {
      const { aspect, clipId } = action;
      const existing = { ...state.timeline.aspects[aspect]?.clipFraming };
      delete existing[clipId];
      const aspectsNext = {
        ...state.timeline.aspects,
        [aspect]: { clipFraming: existing },
      };
      return markDirty(state, { ...state.timeline, aspects: aspectsNext });
    }

    case 'ADD_ASSET':
      return { ...state, assets: [...state.assets, action.asset] };

    case 'REORDER_ASSETS': {
      const { fromIdx, toIdx } = action;
      if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return state;
      if (toIdx >= state.assets.length) return state;
      const next = state.assets.slice();
      const [picked] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, picked);
      return { ...state, assets: next };
    }

    case 'SET_RENDERS':
      return { ...state, renders: action.renders };

    case 'DELETE_ASSET': {
      const assetId = action.assetId;
      const timeline: Timeline = {
        ...state.timeline,
        video: { clips: state.timeline.video.clips.filter((c) => c.assetId !== assetId) },
        audio: { clips: state.timeline.audio.clips.filter((c) => c.assetId !== assetId) },
      };
      return markDirty(
        { ...state, assets: state.assets.filter((a) => a.id !== assetId) },
        timeline,
      );
    }

    case 'ADD_CLIP': {
      const { asset } = action;
      const track: TrackKind = asset.kind === 'audio' ? 'audio' : 'video';
      const list = state.timeline[track].clips;
      /* Only the audio track has a cap (1, render-pipeline limit).
       * Video accepts any number of clips. */
      if (track === 'audio' && list.length >= MAX_AUDIO_CLIPS) return state;
      /* Default placement: video clips chain to the end of the existing
       * sequence; audio drops at t=0. Callers can override via
       * action.startAt — used when the user clicks a specific spot on
       * the audio track to drop a new clip there. */
      const startAt = action.startAt !== undefined
        ? Math.max(0, action.startAt)
        : (track === 'video'
            ? list.reduce((acc, c) => Math.max(acc, c.start + clipDur(c)), 0)
            : 0);
      const clip = buildClipFromAsset(asset, track, startAt);
      const nextList = [...list, clip] as VideoClip[] | AudioClip[];
      const timeline: Timeline = {
        ...state.timeline,
        [track]: { clips: nextList },
        /* New visual clips default to "cover" per aspect — fill the
         * canvas, crop the overflow — instead of letterboxing. */
        aspects: track === 'video'
          ? withCoverFraming(state.timeline.aspects, clip.id, asset.width, asset.height)
          : state.timeline.aspects,
      } as Timeline;
      /* Intentionally do NOT auto-select the new clip — the inspector
       * modal should only open when the user explicitly taps a clip,
       * not as a side effect of a drag-drop / picker add. */
      return markDirty(state, timeline);
    }

    case 'ADD_TEXT': {
      const d = loadTextDefaults();
      const clip: TextClip = {
        id: uid('tc'),
        text: 'Your text',
        trimIn: 0,
        trimOut: 3,
        start: Math.max(0, action.startAt),
        fontSize: d.fontSize ?? 0.07,
        color: d.color ?? '#ffffff',
        font: d.font ?? 'sans',
        outlineColor: d.outlineColor ?? '#000000',
        bgColor: d.bgColor ?? '',
        bgRadius: d.bgRadius ?? 1,
        textOffsetY: d.textOffsetY ?? 0,
      };
      const timeline: Timeline = {
        ...state.timeline,
        overlay: { clips: [...state.timeline.overlay.clips, clip] },
      };
      /* Unlike media clips, a fresh text clip is a placeholder — select
       * it so the inspector opens for immediate editing. */
      return {
        ...markDirty(state, timeline),
        selectedClip: { track: 'overlay', id: clip.id },
      };
    }

    case 'MOVE_CLIP':
    case 'UPDATE_CLIP':
    case 'TRIM_CLIP': {
      const { track, clipId, patch } = action;
      const list = state.timeline[track].clips as AnyClip[];
      const next = list.map((c) => (c.id === clipId ? ({ ...c, ...patch } as AnyClip) : c));
      const timeline: Timeline = {
        ...state.timeline,
        [track]: { clips: next },
      } as Timeline;
      return markDirty(state, timeline);
    }

    case 'DELETE_CLIP': {
      const { track, clipId } = action;
      const next = state.timeline[track].clips.filter((c) => c.id !== clipId);
      const timeline: Timeline = {
        ...state.timeline,
        [track]: { clips: next },
      } as Timeline;
      return {
        ...markDirty(state, timeline),
        selectedClip: state.selectedClip?.id === clipId ? null : state.selectedClip,
      };
    }

    case 'SPLIT_CLIP': {
      const { track, clipId } = action;
      const list = state.timeline[track].clips as AnyClip[];
      const idx = list.findIndex((c) => c.id === clipId);
      if (idx < 0) return state;
      const clip = list[idx];
      const dur = clipDur(clip);
      if (dur < MIN_CLIP_DUR_FOR_SPLIT) return state;
      const halfDur = dur / 2;
      const mid = clip.trimIn + halfDur;
      const firstId = uid(track === 'audio' ? 'ac' : 'vc');
      const secondId = uid(track === 'audio' ? 'ac' : 'vc');
      const first = { ...clip, id: firstId, trimOut: mid } as AnyClip;
      const second = { ...clip, id: secondId, trimIn: mid, start: clip.start + halfDur } as AnyClip;
      /* New clip from a split shouldn't inherit a "transitionIn" cut/
       * crossfade from nowhere — for the second half, force a cut so
       * the seam between the two halves is clean. */
      if (track === 'video' && 'transitionIn' in second) {
        (second as VideoClip).transitionIn = { type: 'cut', duration: 0 };
      }
      const next = [...list.slice(0, idx), first, second, ...list.slice(idx + 1)];
      const timeline: Timeline = {
        ...state.timeline,
        [track]: { clips: next },
      } as Timeline;
      /* The pre-split clip id is gone — if it was the selected one,
       * clear the selection so the inspector closes (it can't find
       * the clip anyway). Don't auto-select either half: the user
       * picked Split from the right-click menu, not "open settings". */
      const wasSelected = state.selectedClip?.id === clipId;
      return {
        ...markDirty(state, timeline),
        selectedClip: wasSelected ? null : state.selectedClip,
      };
    }

    case 'SELECT_CLIP':
      return { ...state, selectedClip: action.selection };

    case 'SEEK':
      return { ...state, playhead: Math.max(0, Math.min(action.t, state.timeline.project.duration)) };

    case 'SET_PLAYING':
      return { ...state, playing: action.playing };

    case 'SET_PX_PER_SEC':
      return { ...state, pxPerSec: Math.max(10, Math.min(action.px, 400)) };

    case 'MARK_SAVING':
      return { ...state, saveState: { ...state.saveState, saving: true, error: null } };

    case 'MARK_SAVED':
      return { ...state, saveState: { dirty: false, saving: false, lastSavedAt: Date.now(), error: null } };

    case 'MARK_SAVE_ERROR':
      return { ...state, saveState: { ...state.saveState, saving: false, error: action.error } };

    case 'SET_RENDER':
      return { ...state, renderState: { ...state.renderState, ...action.patch } };

    case 'BUMP_RENDER_ATTEMPT':
      return {
        ...state,
        renderAttempts: {
          ...state.renderAttempts,
          [action.aspect]: (state.renderAttempts[action.aspect] || 0) + 1,
        },
        // Keep the legacy single renderState in sync with the active
        // aspect — it's what the bottom-of-preview status text reads.
        renderState: action.aspect === state.currentAspect ? {
          ...state.renderState,
          attempt: state.renderState.attempt + 1,
          status: 'queued', percent: 0, message: '', error: null,
        } : state.renderState,
      };

    default:
      return state;
  }
}

// ---------- Hook ----------

export interface EditorActions {
  loadProject: (project: VideoProject) => void;
  setMeta: (patch: Partial<EditorMeta>) => void;
  setCurrentAspect: (aspect: AspectRatio) => void;
  updateFraming: (aspect: AspectRatio, clipId: string, patch: Partial<ClipFraming>) => void;
  resetFraming: (aspect: AspectRatio, clipId: string) => void;
  setRenders: (renders: ProjectRender[]) => void;
  addAsset: (asset: ProjectAsset) => void;
  reorderAssets: (fromIdx: number, toIdx: number) => void;
  deleteAsset: (assetId: number) => void;
  addClipFromAsset: (asset: ProjectAsset, startAt?: number) => void;
  addTextAt: (startAt: number) => void;
  moveClip: (track: TrackKind, clipId: string, patch: Partial<AnyClip>) => void;
  updateClip: (track: TrackKind, clipId: string, patch: Partial<AnyClip>) => void;
  trimClip: (track: TrackKind, clipId: string, patch: Partial<AnyClip>) => void;
  deleteClip: (track: TrackKind, clipId: string) => void;
  splitClip: (track: TrackKind, clipId: string) => void;
  selectClip: (selection: ClipSelection | null) => void;
  seek: (t: number) => void;
  setPlaying: (playing: boolean) => void;
  setPxPerSec: (px: number) => void;
  markSaving: () => void;
  markSaved: () => void;
  markSaveError: (error: string) => void;
  setRender: (patch: Partial<RenderState>) => void;
  bumpRenderAttempt: (aspect: AspectRatio) => void;
}

export default function useEditorState(): [EditorState, EditorActions] {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Each individual action callback is stable across renders (useCallback
  // deps are empty + dispatch is guaranteed stable by useReducer). The
  // surrounding `actions` object must ALSO be stable so consumers that
  // put it in useEffect/useCallback dep arrays don't fire every render.
  // Without useMemo the object literal would be a fresh reference each
  // time and useRenderProgress would tear down + reopen its SSE
  // connection on every render of EditorPage. Same hazard for autosave.
  const loadProject = useCallback((project: VideoProject) => dispatch({ type: 'LOAD_PROJECT', project }), []);
  const setMeta = useCallback((patch: Partial<EditorMeta>) => dispatch({ type: 'SET_META', patch }), []);
  const setCurrentAspect = useCallback((aspect: AspectRatio) =>
    dispatch({ type: 'SET_CURRENT_ASPECT', aspect }), []);
  const updateFraming = useCallback((aspect: AspectRatio, clipId: string, patch: Partial<ClipFraming>) =>
    dispatch({ type: 'UPDATE_FRAMING', aspect, clipId, patch }), []);
  const resetFraming = useCallback((aspect: AspectRatio, clipId: string) =>
    dispatch({ type: 'RESET_FRAMING', aspect, clipId }), []);
  const setRenders = useCallback((renders: ProjectRender[]) =>
    dispatch({ type: 'SET_RENDERS', renders }), []);
  const addAsset = useCallback((asset: ProjectAsset) => dispatch({ type: 'ADD_ASSET', asset }), []);
  const reorderAssets = useCallback((fromIdx: number, toIdx: number) =>
    dispatch({ type: 'REORDER_ASSETS', fromIdx, toIdx }), []);
  const deleteAsset = useCallback((assetId: number) => dispatch({ type: 'DELETE_ASSET', assetId }), []);
  const addClipFromAsset = useCallback(
    (asset: ProjectAsset, startAt?: number) =>
      dispatch({ type: 'ADD_CLIP', asset, startAt }),
    [],
  );
  const addTextAt = useCallback((startAt: number) =>
    dispatch({ type: 'ADD_TEXT', startAt }), []);
  const moveClip = useCallback((track: TrackKind, clipId: string, patch: Partial<AnyClip>) =>
    dispatch({ type: 'MOVE_CLIP', track, clipId, patch }), []);
  const updateClip = useCallback((track: TrackKind, clipId: string, patch: Partial<AnyClip>) =>
    dispatch({ type: 'UPDATE_CLIP', track, clipId, patch }), []);
  const trimClip = useCallback((track: TrackKind, clipId: string, patch: Partial<AnyClip>) =>
    dispatch({ type: 'TRIM_CLIP', track, clipId, patch }), []);
  const deleteClip = useCallback((track: TrackKind, clipId: string) =>
    dispatch({ type: 'DELETE_CLIP', track, clipId }), []);
  const splitClip = useCallback((track: TrackKind, clipId: string) =>
    dispatch({ type: 'SPLIT_CLIP', track, clipId }), []);
  const selectClip = useCallback((selection: ClipSelection | null) => dispatch({ type: 'SELECT_CLIP', selection }), []);
  const seek = useCallback((t: number) => dispatch({ type: 'SEEK', t }), []);
  const setPlaying = useCallback((playing: boolean) => dispatch({ type: 'SET_PLAYING', playing }), []);
  const setPxPerSec = useCallback((px: number) => dispatch({ type: 'SET_PX_PER_SEC', px }), []);
  const markSaving = useCallback(() => dispatch({ type: 'MARK_SAVING' }), []);
  const markSaved = useCallback(() => dispatch({ type: 'MARK_SAVED' }), []);
  const markSaveError = useCallback((error: string) => dispatch({ type: 'MARK_SAVE_ERROR', error }), []);
  const setRender = useCallback((patch: Partial<RenderState>) => dispatch({ type: 'SET_RENDER', patch }), []);
  const bumpRenderAttempt = useCallback((aspect: AspectRatio) =>
    dispatch({ type: 'BUMP_RENDER_ATTEMPT', aspect }), []);

  const actions = useMemo<EditorActions>(() => ({
    loadProject, setMeta, setCurrentAspect, updateFraming, resetFraming, setRenders,
    addAsset, reorderAssets, deleteAsset, addClipFromAsset, addTextAt,
    moveClip, updateClip, trimClip, deleteClip, splitClip, selectClip,
    seek, setPlaying, setPxPerSec,
    markSaving, markSaved, markSaveError,
    setRender, bumpRenderAttempt,
  }), [
    loadProject, setMeta, setCurrentAspect, updateFraming, resetFraming, setRenders,
    addAsset, reorderAssets, deleteAsset, addClipFromAsset, addTextAt,
    moveClip, updateClip, trimClip, deleteClip, splitClip, selectClip,
    seek, setPlaying, setPxPerSec,
    markSaving, markSaved, markSaveError,
    setRender, bumpRenderAttempt,
  ]);

  return [state, actions];
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  type IOSWindow = Window & { MSStream?: unknown };
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as IOSWindow).MSStream;
}
